import React, { useEffect } from 'react';
import './MLConsentDialog.css';

// Optional-consent dialog for the ML training license in ToS 4.3. Both exits
// that are NOT an explicit choice (Escape, overlay click) go through
// `onDismiss`, which records nothing — GDPR Art. 4(11) needs a clear
// affirmative action, and inactivity is not one (issue #398). Escape is
// handled here rather than left to the browser because an aria-modal dialog
// with no Escape route traps keyboard users into answering.
const MLConsentDialog = ({ onAccept, onDecline, onDismiss }) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && onDismiss) {
      onDismiss();
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && onDismiss) onDismiss();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onDismiss]);

  return (
    <div className="dialog-overlay" onClick={handleOverlayClick}>
      <div className="ml-consent-dialog" role="dialog" aria-modal="true" aria-labelledby="ml-consent-title">
        <h2 id="ml-consent-title">How Tweedia Uses Your Content</h2>
        <div className="ml-consent-body">
          <p>
            Tweedia can use the content you upload to train and improve our
            AI-powered video editing tools — but only if you say yes. This is
            optional, and every feature works the same either way.
          </p>
          <div className="ml-consent-details">
            <p><strong>If you agree:</strong></p>
            <ul>
              <li>Your uploaded videos and metadata may be used to train machine learning models</li>
              <li>This helps improve video analysis, editing, and content recommendations</li>
              <li>You retain full ownership of everything you upload</li>
              <li>You can withdraw your consent anytime in Account Settings</li>
            </ul>
          </div>
          <p className="ml-consent-legal" data-testid="ml-consent-legal">
            Choosing <strong>Yes, use my content</strong> gives Tweedia Technologies LLC
            permission to use your uploads for AI training as described in our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            {' '}and{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
            Choosing <strong>No, don't use my content</strong> — or closing this dialog
            — does not.
          </p>
        </div>
        {/* Both choices carry identical labels-that-state-the-action and
            identical styling (same class, equal width). GDPR Art. 7(2) /
            Recital 32 and the EDPB dark-pattern guidance treat a refusal that
            is visually harder than acceptance as consent that is not freely
            given — so no primary CTA here, and no "Continue" that reads as
            "proceed" rather than "yes, train on my videos". */}
        <div className="ml-consent-actions">
          <button className="ml-consent-choice" data-testid="ml-consent-decline" onClick={onDecline}>No, don't use my content</button>
          <button className="ml-consent-choice" data-testid="ml-consent-accept" onClick={onAccept}>Yes, use my content</button>
        </div>
      </div>
    </div>
  );
};

export default MLConsentDialog;
